import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import TurnLeftIcon from "@mui/icons-material/TurnLeft";
// import TurnRightIcon from "@mui/icons-material/TurnRight"
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { IMail} from "src/types/mail";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import {
  GetActiveMailboxProvider,
  GetAllEmails,
  GetEmailById,
  SendEmail,
  setCurrentEmail,
} from "src/redux/features/mailBox/mailbox-slice";
import EmailContent from "./EmailContent";

const urlRegex = /https?:\/\/[^\s"'<>]+/g;

// Extend Quill with a clipboard matcher
export const quillModules = {
  toolbar: [
    [{ bold: true }, { italic: true }, { underline: true }, { strike: true }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["link", "image", "video"],
    [{ background: [] }, { color: [] }],
    ["clean"],
  ],
  clipboard: {
    matchers: [
      [
        Node.TEXT_NODE,
        (node: any, delta: any) => {
          const matches = [...node.data.matchAll(urlRegex)];
          if (matches.length === 0) return delta;

          const newDelta = [];
          let str = node.data;
          let lastIndex = 0;

          for (const match of matches) {
            const url = match[0];
            const index = match.index!;

            if (index > lastIndex) {
              newDelta.push({ insert: str.slice(lastIndex, index) });
            }

            newDelta.push({
              insert: url,
              attributes: { link: url },
            });

            lastIndex = index + url.length;
          }

          if (lastIndex < str.length) {
            newDelta.push({ insert: str.slice(lastIndex) });
          }

          return { ops: newDelta };
        },
      ],
    ],
  },
};

const EmailDetails = () => {
  //#region variables
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  //store the bottom ref of modal
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const [reply, setReply] = useState<boolean>(false);
  const [emailBody, setEmailBody] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);

  const mailData = useAppSelector<IMail | null>(
    (state) => state.mailBoxSlice.currentEmail
  );

  const { isLoading, threads, isThreadsLoading, mailBoxAuthData } =
    useAppSelector((state) => state.mailBoxSlice);

  //#endregion

  //#region functions
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setAttachments([...attachments, ...Array.from(event.target.files)]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleReplyEmail = async () => {
    if (!mailBoxAuthData) {
      return;
    }
    setReply(false);
    await dispatch(
      SendEmail({
        email: mailBoxAuthData.email,
        subject: `Re: ${mailData?.subject}`,
        body: emailBody,
        to: [mailData?.from[0].email ?? ""],
        attachments,
        replyTo: mailData?.from[0].email,
        messageId: mailData?.id,
      })
    );
    //get updated threads
    dispatch(
      GetAllEmails({
        userEmail: mailBoxAuthData.email,
        pageToken: "",
        threadId: mailData?.threadId,
      })
    );
  };

  //#endregion

  //#region useEffect
  useEffect(() => {
    dispatch(GetActiveMailboxProvider());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && mailBoxAuthData?.email) {
      dispatch(GetEmailById({ userEmail: mailBoxAuthData.email, emailId: id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mailBoxAuthData]);

  useEffect(() => {
    if (reply && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [reply]);

  useEffect(() => {
    //get threads
    if (mailData && mailBoxAuthData?.email) {
      dispatch(
        GetAllEmails({
          userEmail: mailBoxAuthData.email,
          pageToken: "",
          threadId: mailData?.threadId,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailData?.threadId]);

  //#endregion

  if (isLoading || isThreadsLoading) {
    return (
      <Box className="customLoader">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="emailDetails-container">
      <div className="emailDetails">
        <Button
          className="back-btn"
          onClick={() => {
            navigate("/mailbox");
            dispatch(setCurrentEmail(null));
          }}
        >
          <KeyboardBackspaceIcon />
          <Typography>Back</Typography>
        </Button>
        <Typography variant="h3" className="emailSubject">
          {mailData?.subject}
        </Typography>
        <EmailContent mailData={mailData} mailBoxAuthData={mailBoxAuthData} />
        <div className="emails-Threads">
          {threads.map((item) => (
            <div className="thread">
              <EmailContent mailData={item} mailBoxAuthData={mailBoxAuthData} />
            </div>
          ))}
        </div>
        <div ref={bottomRef} />
        {!reply && (
          <div className="email-footer">
            <Button
              variant="contained"
              onClick={() => {
                setReply(true);
              }}
            >
              <TurnLeftIcon />
              Reply
            </Button>
            {/* <Button variant="outlined">
              <TurnRightIcon />
              Forward
            </Button> */}
          </div>
        )}

        {reply && (
          <Box className="email-Editor">
            <ReactQuill
              value={emailBody}
              onChange={setEmailBody}
              modules={quillModules}
              placeholder="Compose your email..."
            />
            <Box className="email-attachments">
              {attachments.map((file, index) => (
                <Box key={index} className="email-attachment">
                  <Typography variant="body2" className="attachment-name">
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeAttachment(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            {/* Attachment Section */}
            <div className="editor-footer">
              <div className="flex-gap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleReplyEmail()}
                >
                  Send
                </Button>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <IconButton color="primary" component="span">
                    <AttachFileIcon />
                  </IconButton>
                </label>
              </div>
              <DeleteOutlinedIcon
                className="icon"
                onClick={() => setReply(false)}
              />
            </div>
          </Box>
        )}
      </div>
    </div>
  );
};

export default EmailDetails;
